import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Gift, Copy, Link2, Loader2, Users, MousePointerClick, Tag } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { SEO } from "../components/SEO";
import { FadeUp } from "../components/motion";
import { ShareButtons } from "../components/ShareButtons";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n/LanguageContext";

// Page publique du programme de parrainage : explique le fonctionnement et,
// pour un client connecté, affiche son lien personnel + ses statistiques.
export default function Referral() {
  const { user } = useAuth();
  const { t, ts } = useLang();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!user) { setData(null); return; }
    api.get("/account/referrals").then((r) => setData(r.data)).catch(() => setData(null));
  }, [user]);

  const link = data?.code ? `${window.location.origin}/inscription?ref=${data.code}` : "";

  const copy = async (value, key) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(t("referral.copied"));
    } catch { /* presse-papiers indisponible */ }
  };

  return (
    <div className="pt-32 pb-28 max-w-4xl mx-auto px-6 lg:px-10" data-testid="referral-page">
      <SEO title={`${ts("referral.title")} | Mr Trophée`} description={ts("referral.text")} path="/parrainage" />

      <FadeUp>
        <p className="text-gold tracking-[0.3em] uppercase text-xs mb-4 flex items-center gap-2">
          <Gift className="w-4 h-4" /> {t("referral.page_kicker")}
        </p>
        <h1 className="font-serif text-4xl md:text-5xl leading-tight mb-4" data-testid="referral-page-title">{t("referral.title")}</h1>
        <p className="text-ivory/60 text-sm leading-relaxed max-w-2xl mb-12">{t("referral.text")}</p>
      </FadeUp>

      {/* Comment ça fonctionne */}
      <FadeUp delay={0.08}>
        <h2 className="text-xs uppercase tracking-widest text-ivory/65 mb-4">{t("referral.how_title")}</h2>
        <div className="grid sm:grid-cols-3 gap-4 mb-12">
          {["how_1", "how_2", "how_3"].map((k, i) => (
            <div key={k} className="bg-surface border border-ivory/10 rounded-xl p-5" data-testid={`referral-step-${i}`}>
              <p className="font-serif text-3xl text-gold mb-2">{i + 1}</p>
              <p className="text-sm text-ivory/70 leading-relaxed">{t(`referral.${k}`)}</p>
            </div>
          ))}
        </div>
      </FadeUp>

      {/* Zone personnelle */}
      {user === undefined || (user && !data) ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-gold" /></div>
      ) : !user ? (
        <FadeUp delay={0.12}>
          <div className="bg-surface border border-gold/20 rounded-xl p-8 text-center" data-testid="referral-login-cta">
            <p className="text-sm text-ivory/70 mb-5">{t("referral.cta_login")}</p>
            <Link to="/connexion" state={{ next: "/parrainage" }} data-testid="referral-login-btn"
              className="inline-flex items-center gap-2 bg-gold text-ink font-semibold px-8 py-3.5 rounded-full hover:bg-champagne transition-colors duration-300">
              {t("referral.cta_login_btn")}
            </Link>
          </div>
        </FadeUp>
      ) : data && (
        <FadeUp delay={0.12}>
          <div className="bg-surface border border-gold/20 rounded-xl p-7 mb-8" data-testid="referral-personal">
            <p className="text-xs uppercase tracking-widest text-ivory/65 mb-2">{t("referral.your_link")}</p>
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <code data-testid="referral-page-link"
                className="flex-1 min-w-64 bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm text-champagne truncate">{link}</code>
              <button data-testid="referral-page-copy" onClick={() => copy(link)}
                className="inline-flex items-center gap-2 bg-gold text-ink font-semibold px-5 py-3 rounded-full text-sm hover:bg-champagne transition-colors duration-300">
                <Copy className="w-4 h-4" /> {t("referral.copy")}
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-x-8 gap-y-2 text-sm mb-6">
              <p className="text-ivory/60 inline-flex items-center gap-2">
                <Link2 className="w-4 h-4 text-gold" /> {t("referral.your_code")} :
                <strong className="text-gold tracking-wider" data-testid="referral-page-code">{data.code}</strong>
              </p>
              <p className="text-ivory/60 inline-flex items-center gap-2">
                <Tag className="w-4 h-4 text-gold" /> {t("referral.filleul_code")} :
                <strong className="text-gold tracking-wider" data-testid="referral-ref10">REF10</strong>
              </p>
              <p className="text-ivory/60 inline-flex items-center gap-2" data-testid="referral-page-stats">
                <MousePointerClick className="w-4 h-4 text-gold" /> {data.clicks} ·
                <Users className="w-4 h-4 text-gold" /> {data.referred_count}
              </p>
            </div>
            <ShareButtons url={link} title={ts("referral.title")} label={t("footer.share_page")} />
          </div>

          <div className="bg-surface border border-ivory/10 rounded-xl p-7" data-testid="referral-page-rewards">
            <h2 className="text-xs uppercase tracking-widest text-ivory/65 mb-4">{t("referral.rewards")}</h2>
            {data.rewards.length === 0 ? (
              <p className="text-sm text-ivory/65">{t("referral.no_rewards")}</p>
            ) : (
              <div className="space-y-2">
                {data.rewards.map((r) => (
                  <p key={r.id} data-testid={`referral-page-reward-${r.code}`} className="text-sm text-ivory/70">
                    <strong className="text-gold tracking-wider">{r.code}</strong>
                    {" — "}{r.kind === "pct" ? `−${r.value} %` : `−${r.value} $`}
                    {" · "}{t("referral.valid_until")} {r.valid_to}
                    {r.uses > 0 && <span className="text-ivory/65"> · {t("referral.used")}</span>}
                  </p>
                ))}
              </div>
            )}
          </div>
        </FadeUp>
      )}

      <p className="text-[11px] text-ivory/60 mt-10 text-center" data-testid="referral-note">{t("referral.page_note")}</p>
    </div>
  );
}
